import type { DocCreateOptions } from '@affine/core/modules/doc/types';
import {
  NoteDisplayMode,
  type NoteProps,
  type ParagraphProps,
  type RootBlockProps,
} from '@blocksuite/affine/model';
import type { SurfaceBlockProps } from '@blocksuite/affine/std/gfx';
import { type Store, Text } from '@blocksuite/affine/store';
import { transact } from 'yjs';

const INITIAL_TARGET_FOLDER_ID_META_KEY = 'streamlain:initialTargetFolderId';

export interface DocProps {
  page?: Partial<RootBlockProps>;
  surface?: Partial<SurfaceBlockProps>;
  note?: Partial<NoteProps>;
  paragraph?: Partial<ParagraphProps>;
  onStoreLoad?: (
    doc: Store,
    props: {
      noteId: string;
      paragraphId: string;
      surfaceId: string;
    }
  ) => void;
}

export function initDocFromProps(
  doc: Store,
  props?: DocProps,
  options: DocCreateOptions = {}
) {
  // Folder placement and initial block tree must reach sync as one Yjs update.
  // Without the outer transaction, targetFolderId is pushed first and the
  // block/title initialization second; backend persistence then publishes a
  // redundant create-followed-by-rename metadata wave.
  doc.load(() => {
    let initializedBlockIds:
      | {
          noteId: string;
          paragraphId: string;
          surfaceId: string;
        }
      | undefined;
    transact(
      doc.spaceDoc,
      () => {
        if (options.targetFolderId) {
          doc.spaceDoc
            .getMap('meta')
            .set(INITIAL_TARGET_FOLDER_ID_META_KEY, options.targetFolderId);
        }
        const pageBlockId = doc.addBlock(
          'affine:page',
          props?.page || { title: new Text(options.title || '') }
        );
        const surfaceId = doc.addBlock(
          'affine:surface' as never,
          props?.surface || {},
          pageBlockId
        );
        const noteBlockId = doc.addBlock(
          'affine:note',
          {
            ...props?.note,
            displayMode: NoteDisplayMode.DocAndEdgeless,
          },
          pageBlockId
        );
        const paragraphBlockId = doc.addBlock(
          'affine:paragraph',
          props?.paragraph || {},
          noteBlockId
        );
        initializedBlockIds = {
          noteId: noteBlockId,
          paragraphId: paragraphBlockId,
          surfaceId,
        };
      },
      { force: true }
    );
    if (initializedBlockIds) {
      props?.onStoreLoad?.(doc, initializedBlockIds);
    }
    doc.history.undoManager.clear();
  });
}
